//@author: devin

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    model,
    NgZone,
    OnInit,
    Output,
    ViewEncapsulation,
} from "@angular/core";
import { ItemService } from "../../../util/items/item.service";
import { S25DropdownAbstract } from "../s25.dropdown.abstract";
import { SearchCriteriaType } from "../../../pojo/SearchCriteriaI";
import { SearchCriteriaService } from "../../../services/search/search-criteria/search.criteria.service";
import { DropdownDataUtil } from "../dropdown.data.util";
import { S25Util } from "../../../util/s25-util";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { SearchCriteriaContext } from "../../../services/search/search-criteria/search.criteria.context";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { jSith } from "../../../util/jquery-replacement";
import { Nullable } from "../../../pojo/Util";

@TypeManagerDecorator("s25-ng-dropdown-multi-search-criteria")
@Component({
    selector: "s25-ng-dropdown-multi-search-criteria",
    template: `
        @if (isNonEmptyBean(items) || serverSide) {
            <s25-ng-generic-multiselect-dropdown
                [value]="value()"
                [hasFav]="hasFav"
                [onSelect]="onSelect"
                [items]="items"
                [staticItems]="staticItems"
                [autoOpen]="autoOpen"
                [alwaysOpen]="alwaysOpen"
                [hideSearch]="hideSearch"
                [focusFirst]="false"
                [searchEnabled]="searchEnabled"
                [apiBean]="apiBean"
                [serverSide]="serverSide"
                [placeholder]="placeholder"
                [ariaLabel]="ariaLabel"
                [resetSelectedOnCleanup]="resetSelectedOnCleanup"
                [alwaysShowDone]="alwaysShowDone"
                (valueChange)="onValueChange($event)"
                [onDone]="onDone"
                [hasDone]="hasDone"
                [ariaLabel]="ariaLabel"
                (done)="done.emit($event)"
            />
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25DropdownMultiSearchCriteriaComponent extends S25DropdownAbstract implements OnInit {
    @Input() type: SearchCriteriaType["type"];
    readonly value = model<Nullable<DropDownItem[]>>();
    @Input() onDone?: (value: { items: Nullable<DropDownItem[]> }) => void; // Exists for angularJS integration
    @Input() alwaysShowDone?: boolean = true;
    @Input() placeholder?: string;
    @Input() hasDone: boolean;
    /** When set, overrides {@link SearchCriteriaContextI.expensiveSearch} for this instance. */
    @Input() expensiveSearchOverride?: boolean;
    /** When true, use {@link staticItems} only (e.g. My Events membership org list). */
    @Input() useStaticOnly?: boolean;

    @Output() done = new EventEmitter<Nullable<DropDownItem[]>>();

    private searchCriteriaContext = inject(SearchCriteriaContext);
    readonly itemService = inject(ItemService);

    serviceMethod: Function; // temporary hold for context.serviceMethod when switched for custom GET request (security group category filter)

    static MIN_ITEMS_FOR_SEARCH: number = 10;

    constructor(
        private zone: NgZone,
        private elementRef: ElementRef,
    ) {
        super();
    }
    isNonEmptyBean = S25Util.isNonEmptyBean;

    ngOnInit() {
        const context = this.searchCriteriaContext.Context[this.type];
        if (context) {
            if (!this.placeholder) this.placeholder = "Select from " + context.title;
            this.hasFav = S25Util.toBool(context.showFavorite);

            const value = this.value() ?? [];
            if (value && value.length && value.length > 0) {
                value.forEach((chosen) => {
                    chosen.itemTypeId = context.itemTypeId;
                });
            }

            if (this.useStaticOnly && this.staticItems?.length) {
                this.items = S25Util.deepCopy(this.staticItems);
                this.searchEnabled = this.items.length > S25DropdownMultiSearchCriteriaComponent.MIN_ITEMS_FOR_SEARCH;
                this.onLoad && this.onLoad();
                this.cd.detectChanges();
                return;
            }

            let p: Promise<any>;
            let DDItems: DropDownItem[] = [];

            const expensiveSearch = this.expensiveSearchOverride ?? context.expensiveSearch;

            if (expensiveSearch) {
                this.searchEnabled = true;
                p = context
                    .serviceMethod(
                        //fetch favorites
                        -999, //cacheId
                        0, //pageNum
                        999, //pageSize
                        null, //filter
                        true, //filterFavorites
                        null, //domainFilter
                        this.customFilterValue, //permanent customFilterValue
                    )
                    .then((data: any) => {
                        this.zone.run(() => {
                            this.staticItems = this.staticItems || [];
                            const staticCopy = [].concat(this.staticItems);
                            const favorites = SearchCriteriaService.extractItems(data);

                            jSith.forEach(favorites, (favKey, fav) => {
                                let inStatic = false;

                                jSith.forEach(
                                    staticCopy,
                                    (itemKey, item) => {
                                        if (parseInt(fav.itemId) === parseInt(item.itemId)) {
                                            inStatic = true;
                                            item.favorite = "T";
                                            return true;
                                        }
                                    },
                                    false,
                                    true,
                                );

                                if (!inStatic) {
                                    if (staticCopy.length && staticCopy[0].grp) {
                                        fav.grp = "Starred";
                                    }
                                    this.staticItems.push(fav);
                                }
                            });

                            this.serverSide = DropdownDataUtil.getServerSideModel(
                                context.serviceMethod,
                                this.elementRef.nativeElement,
                                this.customFilterValue,
                            );
                            DDItems = S25Util.deepCopy(this.staticItems);
                        });
                    });
            } else {
                p = this.itemService
                    .criteria(this.type)
                    .fetchAll({
                        params: {
                            useSecurity: S25Util.toBool(this.useSecurity),
                            activeOnly: S25Util.toBool(this.activeOnly),
                            omitReadOnly: S25Util.toBool(this.omitReadOnly),
                            customFilterValue: this.customFilterValue,
                        },
                    })
                    .then((data: DropDownItem[]) => {
                        this.zone.run(() => {
                            const items = SearchCriteriaService.extractItems(data);
                            this.staticItems = items.filter((obj: DropDownItem) => {
                                return obj.favorite === "T";
                            });
                            DDItems = items;
                            this.searchEnabled =
                                DDItems.length > S25DropdownMultiSearchCriteriaComponent.MIN_ITEMS_FOR_SEARCH;
                        });
                    });
            }

            p.then(() => {
                this.items = DDItems;
                this.onLoad && this.onLoad();
                this.cd.detectChanges();
            });
        }
    }
}
